import { motion } from 'framer-motion';

const TM = () => <sup className="text-[0.6em] font-normal align-super">™</sup>;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: 'easeOut' },
  }),
};

const OFFER_ITEMS = [
  { num: '01', label: 'The HPOS Coach Certificate', value: '$4,997' },
  { num: '02', label: '3-Day Live Intensive', value: '$1,997' },
  { num: '03', label: '6 Pre-Recorded Modules', value: '$2,982' },
  { num: '04', label: '90-Day First Client Pipeline System', value: '$3,497' },
  { num: '05', label: 'Weekly Q&A + Office Hours for 12 months', value: '$2,997' },
  { num: '06', label: '1:1 Onboarding with Patrick', value: '$1,997' },
  { num: '07', label: 'HPOS Platform Access for 12 months', value: '$1,997' },
  { num: '08', label: 'Telegram Peer Community', value: '$997' },
  { num: '09', label: '24/7 Support', value: '$997' },
  { num: '10', label: 'HPOS Client Pricing Calculator + Templates', value: '$497', bonus: true },
  { num: '11', label: 'HPOS Coach Directory Listing', value: '$497', bonus: true },
];

const WHAT_COVERED = [
  'Why the coaching market is becoming more saturated',
  'Why AI is changing client expectations',
  'Why coaches need to differentiate with a system, not just another offer',
  'How the Human Potential OS™ 8P framework works as a complete coaching business model',
  'How Profile and Pattern help you understand the client before the first session',
  'How Progress, Pathways, Presence, and Proof help clients follow through between sessions',
  'How Promote and Profit help coaches turn the methodology into a business model',
  'How the AI Coach Clone can support both client transformation and lead generation',
  'How the assessment can be used as a personalised front-end experience',
  'How coaches can begin creating recurring revenue with HPOS-supported coaching',
];

const BIG_SHIFT = [
  'The client gets a personalised profile before the first conversation',
  'The coach can identify patterns faster',
  'The client receives support between sessions',
  'Habits and goals can be tracked',
  'Progress can be measured',
  'AI can support the coach and client without replacing the human relationship',
  'The coach has a clearer pathway to recurring client revenue',
];

export function Day2ReplayPage() {
  return (
    <div
      className="min-h-screen"
      style={{ background: '#f5f0ea', fontFamily: 'Urbanist, sans-serif', color: '#3d2b20' }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-8 md:px-14 pt-8 pb-5 max-w-7xl mx-auto">
        <img
          src="/human_potential_os_logo_V2_cropped.png"
          alt="Human Potential OS"
          className="h-11 object-contain object-left"
        />
        <span className="text-xs tracking-[0.2em] uppercase text-chocolate-500 font-medium hidden sm:block">
          — Foundations Certification · Day 2 of 3 —
        </span>
      </header>
      <div className="mx-8 md:mx-14 h-px bg-chocolate-200 opacity-50 mb-0" />

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="px-8 md:px-14 py-16 md:py-20 max-w-4xl mx-auto">
        <motion.p
          variants={fadeUp} initial="hidden" animate="visible" custom={0}
          className="text-xs tracking-[0.25em] uppercase font-medium mb-5"
          style={{ color: '#8B6347' }}
        >
          — Day 2 Replay · Foundations Certification —
        </motion.p>
        <motion.h1
          variants={fadeUp} initial="hidden" animate="visible" custom={1}
          className="leading-tight mb-4"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(30px, 4vw, 56px)',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            color: '#3d2b20',
          }}
        >
          The New Coaching<br />
          <em style={{ color: '#558347' }}>Business Model.</em>
        </motion.h1>
        <motion.p
          variants={fadeUp} initial="hidden" animate="visible" custom={2}
          className="text-base md:text-lg leading-relaxed max-w-2xl"
          style={{ color: '#6b4c38' }}
        >
          Welcome to your Day 2 replay. In this session, we moved from the deeper Profile and
          Pattern work into the business side of the Human Potential OS<TM /> framework — showing
          how coaches, trainers, and practitioners can begin moving from a session-based model
          into a system-based coaching model.
        </motion.p>
      </section>

      {/* ── Video ─────────────────────────────────────────────── */}
      <section className="px-8 md:px-14 pb-16 max-w-4xl mx-auto">
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={3}
          className="w-full max-w-4xl mx-auto rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 16px 60px rgba(61,43,32,0.18)' }}
        >
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src="https://player.vimeo.com/video/1202262100?h=486c77bb94&badge=0&autopause=0&player_id=0&app_id=58479"
              title="Day 2 Replay — Human Potential OS Foundations Certification"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
            />
          </div>
        </motion.div>
      </section>

      <div className="mx-8 md:mx-14 h-px opacity-30" style={{ background: '#8B6347' }} />

      {/* ── What You'll Discover ───────────────────────────────── */}
      <section className="px-8 md:px-14 py-16 max-w-4xl mx-auto">
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
          style={{ color: '#8B6347' }}
        >
          — What You'll Discover in Day 2 —
        </motion.p>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="mb-8"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(22px, 2.8vw, 36px)',
            fontWeight: 500,
            letterSpacing: '-0.015em',
          }}
        >
          In this session, we covered:
        </motion.h2>
        <ul className="space-y-3">
          {WHAT_COVERED.map((item, i) => (
            <motion.li
              key={i}
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.5}
              className="flex items-start gap-3"
            >
              <span
                className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: '#558347' }}
              />
              <span className="text-base leading-relaxed" style={{ color: '#5c3d2e' }}>{item}</span>
            </motion.li>
          ))}
        </ul>
      </section>

      {/* ── The Big Shift ─────────────────────────────────────── */}
      <section style={{ background: '#3d2b20' }} className="px-8 md:px-14 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
            style={{ color: '#C5A795' }}
          >
            — The Big Shift From Day 2 —
          </motion.p>
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="mb-6"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(22px, 2.8vw, 36px)',
              fontWeight: 500,
              letterSpacing: '-0.015em',
              color: '#f5f0ea',
            }}
          >
            The old model. The new model.
          </motion.h2>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="text-base leading-relaxed mb-10 max-w-2xl"
            style={{ color: '#C5A795' }}
          >
            The old coaching model often depends on one session, one insight, one breakthrough,
            and then hoping the client follows through. Human Potential OS<TM /> creates a coaching
            ecosystem where the client is supported before, during, and after every session.
          </motion.p>
          <ul className="space-y-4">
            {BIG_SHIFT.map((item, i) => (
              <motion.li
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.4}
                className="flex items-start gap-3"
              >
                <span
                  className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: '#558347' }}
                />
                <span className="text-base leading-relaxed" style={{ color: '#e8ddd5' }}>{item}</span>
              </motion.li>
            ))}
          </ul>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={8}
            className="mt-10 text-base leading-relaxed max-w-2xl"
            style={{ color: '#C5A795' }}
          >
            This is why Human Potential OS<TM /> is not just software. It is a methodology, a
            coaching framework, a client support system, and a business growth model for the
            new era of coaching.
          </motion.p>
        </div>
      </section>

      {/* ── Revenue Calculator CTA ────────────────────────────── */}
      <section className="px-8 md:px-14 py-16 max-w-5xl mx-auto">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="max-w-4xl mx-auto rounded-2xl p-8 md:p-12"
          style={{ background: '#e8ddd5', border: '1px solid #C5A795' }}
        >
          <p className="text-xs tracking-[0.25em] uppercase font-medium mb-3" style={{ color: '#8B6347' }}>
            — Try It —
          </p>
          <h2
            className="mb-4"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(20px, 2.5vw, 32px)',
              fontWeight: 500,
              letterSpacing: '-0.015em',
              color: '#3d2b20',
            }}
          >
            The Human Potential OS<TM /> Revenue Calculator
          </h2>
          <p className="text-base leading-relaxed mb-2 max-w-xl" style={{ color: '#6b4c38' }}>
            Use the calculator to explore how the numbers could work in your own coaching business —
            client numbers, pricing, and offer structure affecting your monthly recurring revenue.
          </p>
          <p className="text-xs mb-8 leading-relaxed max-w-xl" style={{ color: '#8B6347' }}>
            For illustration and planning purposes only. Does not guarantee income. Actual results
            depend on your offer, audience, pricing, follow-up, sales activity, and client delivery.
          </p>
          <a
            href="/revenuecalculator"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all hover:opacity-90"
            style={{ background: '#558347', color: '#fff' }}
          >
            Open the Revenue Calculator
          </a>
        </motion.div>
      </section>

      <div className="mx-8 md:mx-14 h-px opacity-30" style={{ background: '#8B6347' }} />

      {/* ── Offer Stack ───────────────────────────────────────── */}
      <section className="px-8 md:px-14 py-16 md:py-20 max-w-5xl mx-auto">
        <div className="max-w-5xl mx-auto">
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
            style={{ color: '#8B6347' }}
          >
            — Join the Human Potential OS<TM /> Coach Certification —
          </motion.p>
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="mb-4"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(24px, 3vw, 42px)',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              color: '#3d2b20',
            }}
          >
            Turn the methodology into something<br className="hidden md:block" />
            <em style={{ color: '#558347' }}>you can actually use.</em>
          </motion.h2>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="text-base leading-relaxed max-w-2xl mb-14"
            style={{ color: '#6b4c38' }}
          >
            The goal is not just to give you more information. The goal is to help you turn the
            methodology into something practical, usable, and valuable in your coaching business —
            with the platform, training, implementation support, client pipeline system, and tools
            to help you begin building a recurring coaching model.
          </motion.p>

          {/* Offer stack image — full width */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
            className="w-full rounded-2xl overflow-hidden mb-16"
            style={{ boxShadow: '0 12px 50px rgba(61,43,32,0.15)' }}
          >
            <img
              src="/Screenshot_2026-06-17_at_21.30.25.png"
              alt="Human Potential OS Coach Certification — Full Offer Stack"
              className="w-full h-auto block"
            />
          </motion.div>

          {/* Offer items list */}
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xs tracking-[0.25em] uppercase font-medium mb-2"
            style={{ color: '#8B6347' }}
          >
            — What You're Getting —
          </motion.p>
          <motion.h3
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="mb-8"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(18px, 2vw, 28px)',
              fontWeight: 500,
              color: '#3d2b20',
            }}
          >
            Inside the Human Potential OS<TM /> Coach Certification:
          </motion.h3>

          <div className="space-y-0 mb-10">
            {OFFER_ITEMS.map((item, i) => (
              <motion.div
                key={item.num}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.3}
                className="flex items-center justify-between py-4 border-b"
                style={{ borderColor: '#C5A795', opacity: 1 }}
              >
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs font-semibold tabular-nums w-6 flex-shrink-0"
                    style={{ color: '#C5A795' }}
                  >
                    {item.num}
                  </span>
                  <span className="text-sm md:text-base" style={{ color: '#3d2b20' }}>
                    {item.label}
                    {item.bonus && (
                      <em className="ml-2 text-xs" style={{ color: '#558347' }}>(bonus)</em>
                    )}
                  </span>
                </div>
                <span className="text-sm font-semibold tabular-nums ml-4 flex-shrink-0" style={{ color: '#8B6347' }}>
                  {item.value}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Stack value summary */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="rounded-2xl p-8 md:p-10 text-center"
            style={{ background: '#3d2b20' }}
          >
            <p className="text-xs tracking-[0.25em] uppercase font-medium mb-2" style={{ color: '#C5A795' }}>
              Total Stack Value
            </p>
            <p
              className="mb-4"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 'clamp(40px, 6vw, 72px)',
                fontWeight: 700,
                color: '#f5f0ea',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              $23,452
            </p>
            <div className="w-12 h-0.5 mx-auto mb-4" style={{ background: '#8B6347' }} />
            <p className="text-xs tracking-[0.2em] uppercase font-medium mb-1" style={{ color: '#C5A795' }}>
              Today
            </p>
            <p
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 'clamp(32px, 4.5vw, 56px)',
                fontWeight: 700,
                color: '#f5f0ea',
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}
            >
              $1,997
            </p>
            <p className="mt-2 text-sm italic" style={{ color: '#C5A795' }}>
              or 3 monthly payments of $797
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing Cards ─────────────────────────────────────── */}
      <section style={{ background: '#e8ddd5' }} className="px-8 md:px-14 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
            style={{ color: '#8B6347' }}
          >
            — Choose Your Payment Option —
          </motion.p>
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="mb-12"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(22px, 2.8vw, 36px)',
              fontWeight: 500,
              letterSpacing: '-0.015em',
              color: '#3d2b20',
            }}
          >
            Join the Human Potential OS<TM /> Coach Certification
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pay in Full */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
              className="rounded-2xl p-8 flex flex-col"
              style={{ background: '#f5f0ea', border: '1px solid #C5A795' }}
            >
              <p className="text-xs tracking-[0.2em] uppercase font-medium mb-4" style={{ color: '#8B6347' }}>
                Pay in Full
              </p>
              <p
                className="mb-1"
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: 48,
                  fontWeight: 700,
                  color: '#3d2b20',
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                }}
              >
                $1,997
              </p>
              <p className="text-sm mb-8" style={{ color: '#8B6347' }}>one payment · best value</p>
              <p className="text-sm leading-relaxed mb-8 flex-1" style={{ color: '#6b4c38' }}>
                Full access to the Human Potential OS<TM /> Coach Certification — platform,
                training, 1:1 onboarding, pipeline system, and 12 months of support.
              </p>
              <a
                href="https://buy.stripe.com/14A9ATcRN76S2J790v43S0l"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all hover:opacity-90"
                style={{ background: '#558347', color: '#fff' }}
              >
                Join Now — $1,997
              </a>
            </motion.div>

            {/* Payment Plan */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
              className="rounded-2xl p-8 flex flex-col"
              style={{ background: '#3d2b20' }}
            >
              <p className="text-xs tracking-[0.2em] uppercase font-medium mb-4" style={{ color: '#C5A795' }}>
                Payment Plan
              </p>
              <p
                className="mb-1"
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: 48,
                  fontWeight: 700,
                  color: '#f5f0ea',
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                }}
              >
                $797
              </p>
              <p className="text-sm mb-8" style={{ color: '#C5A795' }}>× 3 monthly payments</p>
              <p className="text-sm leading-relaxed mb-8 flex-1" style={{ color: '#C5A795' }}>
                Same full access to the Human Potential OS<TM /> Coach Certification, spread
                over 3 monthly payments for added flexibility.
              </p>
              <a
                href="https://buy.stripe.com/8x26oH9FB8aWgzX7Wr43S0n"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all hover:opacity-90"
                style={{ background: '#558347', color: '#fff' }}
              >
                Join Now — 3 × $797
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── What Happens After You Join ───────────────────────── */}
      <section className="px-8 md:px-14 py-16 max-w-4xl mx-auto">
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
          style={{ color: '#8B6347' }}
        >
          — What Happens After You Join —
        </motion.p>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="mb-6"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(20px, 2.5vw, 32px)',
            fontWeight: 500,
            letterSpacing: '-0.015em',
            color: '#3d2b20',
          }}
        >
          You'll be supported from day one.
        </motion.h2>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
          className="space-y-4 text-base leading-relaxed max-w-2xl"
          style={{ color: '#6b4c38' }}
        >
          <p>
            Once you join, you'll get access to the next stage of the Human Potential OS<TM /> Coach
            Certification experience — including the training, implementation support, platform access,
            and resources designed to help you start applying the system with clients.
          </p>
          <p>
            You'll be supported in understanding the methodology, setting up your HPOS-supported
            coaching offer, using the platform, and moving toward your first paying HPOS-supported clients.
          </p>
          <p>
            The goal is not just to give you another certification. The goal is to help you turn the
            system into something practical, usable, and valuable in your coaching business.
          </p>
        </motion.div>
      </section>

      {/* ── Day 3 Banner ──────────────────────────────────────── */}
      <section
        className="px-8 md:px-14 py-16 md:py-20"
        style={{ background: '#558347' }}
      >
        <div className="max-w-4xl mx-auto">
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
            style={{ color: '#c8e0c0' }}
          >
            — Attend Day 3 Live —
          </motion.p>
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="mb-6"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(24px, 3vw, 42px)',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              color: '#f5f0ea',
            }}
          >
            Day 3 — Progress, Pathways,<br className="hidden md:block" />
            Presence &amp; Proof.
          </motion.h2>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="text-base leading-relaxed max-w-2xl mb-8"
            style={{ color: '#c8e0c0' }}
          >
            Day 3 brings together the practical "Doing" side of the Human Potential OS<TM /> framework.
            Insight alone is not enough — Day 3 is where we go into the elements that help turn
            insight into action, support clients between sessions, track transformation, and make
            change more visible.
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          >
            {['Progress', 'Pathways', 'Presence', 'Proof'].map((p) => (
              <div
                key={p}
                className="rounded-xl px-5 py-4 text-center"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                <span
                  style={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: 18,
                    fontWeight: 500,
                    color: '#f5f0ea',
                  }}
                >
                  {p}
                </span>
              </div>
            ))}
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={4}
            className="rounded-xl px-6 py-5 inline-block"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-medium mb-1" style={{ color: '#c8e0c0' }}>
              Time
            </p>
            <p className="text-base font-semibold" style={{ color: '#f5f0ea' }}>
              10:00 AM – 1:00 PM EST
            </p>
            <p className="text-sm" style={{ color: '#c8e0c0' }}>
              3:00 PM – 6:00 PM London / Ireland / UK
            </p>
            <p className="mt-2 text-xs italic" style={{ color: '#c8e0c0' }}>
              Zoom details will be sent to you by email.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Before Day 3 ──────────────────────────────────────── */}
      <section className="px-8 md:px-14 py-16 max-w-4xl mx-auto">
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="text-xs tracking-[0.25em] uppercase font-medium mb-3"
          style={{ color: '#8B6347' }}
        >
          — Before Day 3 —
        </motion.p>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="mb-6"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(20px, 2.5vw, 32px)',
            fontWeight: 500,
            letterSpacing: '-0.015em',
            color: '#3d2b20',
          }}
        >
          Complete your Assessment.
        </motion.h2>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
          className="space-y-4 text-base leading-relaxed max-w-2xl mb-10"
          style={{ color: '#6b4c38' }}
        >
          <p>
            Please complete your Human Potential OS<TM /> Assessment if you haven't already.
            Your assessment code and link will be sent to you by email.
          </p>
          <p>
            Then watch the Day 2 replay above so you can see how the business model, the
            platform, the 8P framework, and the revenue calculator connect together.
          </p>
          <p>See you on Day 3.</p>
        </motion.div>

        {/* Sign off */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
          className="flex items-center gap-5"
        >
          <div
            className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0"
            style={{ boxShadow: '0 4px 16px rgba(61,43,32,0.2)' }}
          >
            <img
              src="/Patrick_Profile_Under_2MB_q95.jpg"
              alt="Patrick Dahdal"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: '#3d2b20' }}>Patrick Dahdal</p>
            <p className="text-sm" style={{ color: '#8B6347' }}>Founder, Human Potential OS<TM /></p>
          </div>
        </motion.div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="px-8 md:px-14 py-8 border-t max-w-7xl mx-auto" style={{ borderColor: '#C5A795' }}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <img
            src="/human_potential_os_logo_V2_cropped.png"
            alt="Human Potential OS"
            className="h-8 object-contain object-left opacity-60"
          />
          <p className="text-xs" style={{ color: '#8B6347' }}>
            humanpotentialos.com · Human Potential OS<TM /> · All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
